import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import moment from 'moment';
import MapTrack from '../components/Map/MapTrack';
import { getCoordsFromTime, getTimeFromCoords } from '../utils';
import * as actions from '../actions/tools';

const userSelector = ({ map }, { track }) =>
  map.usersMap[track.trackId];

const selectedUserSelector = state =>
  state.map.selectedUser;

const selectedDaySelector = state =>
  state.map.selectedDay;

const trackSelectedSelector = createSelector(
  selectedUserSelector,
  (state, { track }) => track,
  (selectedUser, track) => selectedUser !== undefined && selectedUser.id === track.trackId,
);

const strokeWidthSelector = createSelector(
  trackSelectedSelector,
  selected => (selected ? 3.2 : 3),
);

const trackColorSelector = createSelector(
  trackSelectedSelector,
  selected => (selected ? '#502020' : '#303030'),
);

const mediaColorSelector = createSelector(
  trackSelectedSelector,
  selected => (selected ? '#4F009C' : '#000090'),
);

const trackVisibleSelector = createSelector(
  trackSelectedSelector,
  ({ map }) => map.selectionMode === 'multi',
  (selected, multi) => multi || selected,
);

const getStartOfNow = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now.getTime();
};

const onlineSelector = createSelector(
  selectedDaySelector,
  selectedDay => selectedDay &&
    selectedDay.date.getTime() === getStartOfNow(),
);

const placemarkVisibleSelector = createSelector(
  trackVisibleSelector,
  trackSelectedSelector,
  selectedUserSelector,
  (state, { mapZoom }) => mapZoom > 9,
  onlineSelector,
  (visible, selected, selectedUser, zoomed, online) =>
    visible && (selectedUser === undefined || selected) && zoomed && online,
);

const withCaptionSelector = createSelector(
  trackSelectedSelector,
  (state, { mapZoom }) => mapZoom > 11,
  (selected, zoomed) => ((selected || zoomed) ? 'WithCaption' : ''),
);

const videoToolSelectedSelector = ({ tools }) =>
  tools.videoToolSelected;

const videoPlacemarkVisibleSelector = createSelector(
  trackSelectedSelector,
  videoToolSelectedSelector,
  (trackSelected, videoToolSelected) => trackSelected && videoToolSelected,
);

const mediaIndexSelector = ({ tools }) =>
  tools.selectedMediaIndex;

const mediasSelector = (state, { track }) =>
  track && track.medias;

const mediaSelector = createSelector(
  mediasSelector,
  mediaIndexSelector,
  (medias, index) => (medias && index !== undefined) && medias[index],
);

const absoluteTimeSelector = createSelector(
  videoPlacemarkVisibleSelector,
  mediaSelector,
  ({ video }) => video,
  (videoPlacemarkVisible, media, video) => {
    if (videoPlacemarkVisible && media && video.time) {
      const t0 = media.start_time;
      const t1 = media.end_time;
      const alpha = video.duration > 0
        ? video.time / video.duration
        : 0;
      return t0 + ((t1 - t0) * alpha);
    }
    return undefined;
  },
);

const coordsSelector = createSelector(
  mediaSelector,
  media => media && (media.trackObject && media.trackObject.data),
);

const videoPlacemarkLocationSelector = createSelector(
  absoluteTimeSelector,
  coordsSelector,
  (absoluteTime, coords) => {
    if (absoluteTime && coords) {
      return getCoordsFromTime(absoluteTime, coords);
    }
    return undefined;
  },
);

const videoPlacemarkCaptionSelector = createSelector(
  videoPlacemarkLocationSelector,
  location => location && moment(location[2]).format('HH:mm'),
);

const placemarkCaptionSelector = createSelector(
  userSelector,
  user => user && user.name.replace(user.id, '').trim(),
);

const placemarkLocationSelector = (state, { track }) =>
  track.stats.lastLocation;

const lastActivitySelector = (state, { track }) =>
  track.stats.lastActivity;

const placemarkColorSelector = createSelector(
  placemarkLocationSelector,
  lastActivitySelector,
  (location, lastActivity) => {
    if (location) {
      if ((Date.now() - lastActivity) > 10 * 60000) {
        return 'black';
      }
      const flags = location[5];
      if (flags >= 3) {
        return 'red';
      }
      return 'blue';
    }
    return 'black';
  },
);

const mapStateToProps = (state, props) => ({
  user: userSelector(state, props),
  selectedUser: selectedUserSelector(state, props),
  opacity: 1,
  visible: trackVisibleSelector(state, props),
  strokeWidth: strokeWidthSelector(state, props),
  trackColor: trackColorSelector(state, props),
  mediaColor: mediaColorSelector(state, props),
  placemarkLocation: placemarkLocationSelector(state, props),
  placemarkVisible: placemarkVisibleSelector(state, props),
  placemarkCaption: placemarkCaptionSelector(state, props),
  withCaption: withCaptionSelector(state, props),
  videoPlacemarkVisible: videoPlacemarkVisibleSelector(state, props),
  videoPlacemarkLocation: videoPlacemarkLocationSelector(state, props),
  videoPlacemarkCaption: videoPlacemarkCaptionSelector(state, props),
  videoPlacemarkColor: placemarkColorSelector(state, props),
  hintContent: props.track.hint,
  media: mediaSelector(state, props),
});

const mapDispatchToProps = (dispatch, props) => ({
  onTrackClick: () => {
    dispatch(actions.selectTrack(props.track));
  },
  onMediaClick: (event, object) => {
    const coords = event.get('coords');
    const absoluteTime = getTimeFromCoords(coords, object.data);
    dispatch(actions.selectMedia(props.track, object.props, absoluteTime));
  },
  onMouseMove: (event, object) => {
    const coords = event.get('coords');
    const absoluteTime = getTimeFromCoords(coords, object.data);
    dispatch(actions.setTrackHint(props.track, absoluteTime));
  },
  onPlacemarkMouseMove: () => {
    const absoluteTime = props.track.stats.lastLocation[2];
    dispatch(actions.setTrackHint(props.track, absoluteTime));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MapTrack);



// WEBPACK FOOTER //
// ./src/containers/MapTrack.js